import loadable from '@loadable/component';
import { BpkVessel } from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkSnippet from '@skyscanner-internal/backpack-web/bpk-component-snippet';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';
import { redirect } from 'saddlebag-browser';

import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type { ImageSnippetProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/ImageSnippetProps';

import STYLES from './ImageSnippet.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

const ImageSnippet = (props: ImageSnippetProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const {
    altText,
    bodyText,
    buttonText,
    headline,
    imageSrc,
    redirectUrl,
    subtitle,
  } = props.imageSnippetData;

  if (!imageSrc) {
    return null;
  }

  const { desktopLayout } = props;

  const handleClick = () => {
    if (redirectUrl) {
      redirect(redirectUrl);
    }
  };

  return (
    // BpkVessel retained: SCSS `button { min-height: 3rem }` is load-bearing — it
    // keeps BpkSnippet's internal button at 48px instead of the default 36px.
    // Verified: removing it produces a visible 12px button-height reduction.
    // No BpkSnippet prop controls button size, so the child selector must stay.
    <BpkVessel
      className={STYLES.ImageSnippet}
      data-testid="ImageSnippet"
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
    >
      <BpkSnippet
        src={imageSrc}
        altText={altText}
        headline={headline}
        subheading={subtitle}
        bodyText={redirectUrl ? bodyText : undefined}
        desktopLayout={desktopLayout}
        buttonText={buttonText}
        headlineStyle="hero-5"
        onClick={() => handleClick()}
      />
    </BpkVessel>
  );
};

export default (props: ImageSnippetProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.IMAGE_SNIPPET,
      })
    }
  >
    <ImageSnippet {...props} />
  </IntersectionObserverWrapper>
);
